<template>
  <v-menu v-model="menu" :close-on-content-click="false" offset-y max-width="560">
    <template v-slot:activator="{ on, attrs }">
      <v-text-field
        v-bind="attrs" v-on="on"
        :label="label"
        :value="summaryText"
        readonly clearable
        outlined dense
        prepend-inner-icon="mdi-calendar-week"
        @click:clear="clearAll"
      />
    </template>

    <v-card elevation="10" class="pa-3">
      <v-row class="px-2">
        <v-col cols="6">
          <v-select
            v-model="year" :items="years"
            label="Año" dense outlined hide-details
          />
        </v-col>
        <v-col cols="6" class="d-flex align-end justify-end">
          <v-btn small text color="blue" @click="selectAllVisible">Seleccionar todas</v-btn>
          <v-btn small text color="error" @click="clearVisible">Limpiar</v-btn>
        </v-col>
      </v-row>

      <!-- NUEVO: buscador -->
      <v-row class="px-2">
        <v-col cols="12">
          <v-text-field
            v-model="search"
            label="Buscar semana (ej. W31, agosto, 2025-08, 04/08)"
            dense outlined clearable hide-details
            prepend-inner-icon="mdi-magnify"
          />
        </v-col>
      </v-row>

      <v-alert dense outlined type="info" class="mx-2 mb-2">
        Semanas ISO: <strong>Lunes - Domingo</strong>.
      </v-alert>

      <!-- Lista de semanas -->
      <div class="px-2" style="max-height: 340px; overflow-y: auto;" ref="scrollList">
        <v-list two-line dense>
          <v-list-item
            v-for="w in weeksForYearFiltered"
            :key="`w-${year}-${w}`"
            class="rounded-lg"
            @click="toggleWeek(w)"
          >
            <v-list-item-action>
              <v-checkbox :input-value="isSelected(year, w)" @click.stop readonly />
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title class="font-weight-medium">
                {{ weekLabel(year, w) }}
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ weekRangeLabel(year, w) }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-divider v-for="w in weeksForYearFiltered" :key="`div-${year}-${w}`" />
        </v-list>
      </div>

      <v-divider class="my-2" />

      <div class="px-2">
        <div class="text-caption grey--text mb-1">Seleccionadas:</div>
        <v-chip-group column>
          <v-chip
            v-for="s in value" :key="`${s.year}-${s.week}`"
            small class="ma-1" close @click:close="removeWeek(s.year, s.week)"
          >
            {{ `Semana ${pad2(s.week)} • ${s.etiqueta}` }}
          </v-chip>
        </v-chip-group>
      </div>

      <v-card-actions class="justify-end">
        <v-btn text @click="menu=false">Cerrar</v-btn>
        <v-btn color="primary" depressed @click="emitSelection">Aplicar</v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<script>
import moment from 'moment';

export default {
  name: 'WeekMultiPicker',
  props: {
    value: { // [{ year, week, desde, hasta, etiqueta }]
      type: Array,
      default: () => []
    },
    years: {
      type: Array,
      default: () => {
        const y = new Date().getFullYear();
        return [y - 2, y - 1, y, y + 1];
      }
    },
    label: { type: String, default: 'Semanas (lunes a domingo)' },
  },
  data() {
    return {
      menu: false,
      year: new Date().getFullYear(),
      tempSelection: [], // [{year, week}]
      search: '' // <== NUEVO
    };
  },
  computed: {
    weeksForYear() {
      const lastWeek = this.$moment
        ? this.$moment().isoWeekYear(this.year).endOf('year').isoWeeksInYear()
        : require('moment')().isoWeekYear(this.year).endOf('year').isoWeeksInYear();
      return Array.from({ length: lastWeek }, (_, i) => i + 1);
    },
    weeksForYearFiltered() {
      const q = (this.search || '').trim().toLowerCase();
      if (!q) return this.weeksForYear;
      return this.weeksForYear.filter(w => this.matchesWeek(this.year, w, q));
    },
    summaryText() {
      if (!this.value.length) return '';
      const ys = [...new Set(this.value.map(x => x.year))].sort();
      const count = this.value.length;
      if (ys.length === 1) return `${count} sem. en ${ys[0]}`;
      return `${count} sem. en ${ys[0]}–${ys[ys.length - 1]}`;
    },
  },
  watch: {
    search() { this.jumpIfExact(); },
    year() {
      // sincroniza tempSelection con value del año visible
      this.syncTempWithValue();
    }
  },
  mounted() {
    this.syncTempWithValue();
  },
  methods: {
    pad2(n) { return String(n).padStart(2, '0'); },
    isoRange(year, week) {
      const start = moment().isoWeekYear(year).isoWeek(week).startOf('isoWeek'); // lunes
      const end   = moment(start).endOf('isoWeek'); // domingo
      return {
        desde: start.format('YYYY-MM-DD'),
        hasta: end.format('YYYY-MM-DD'),
        etiqueta: `${start.format('DD MMM')} – ${end.format('DD MMM YYYY')}`
      };
    },
    matchesWeek(year, week, q) {
      const m = require('moment');
      m.locale('es');

      const start = m().isoWeekYear(year).isoWeek(week).startOf('isoWeek');
      const end   = start.clone().endOf('isoWeek');

      const ww = String(week).padStart(2,'0');
      const tokens = [
        `Semana ${ww}`,       // w31
        `${week}`,      // 31
        `${year}`,      // 2025
        start.format('YYYY-MM-DD'),
        end.format('YYYY-MM-DD'),
        start.format('YYYY-MM'),
        end.format('YYYY-MM'),
        start.format('DD/MM/YYYY'),
        end.format('DD/MM/YYYY'),
        start.format('DD MMM').toLowerCase(),
        end.format('DD MMM').toLowerCase(),
        start.format('MMMM').toLowerCase(), // agosto
        end.format('MMMM').toLowerCase(),
        `${ww}-${String(year).slice(-2)}`,   // 31-25
      ];

      return tokens.some(t => t.toLowerCase().includes(q));
    },

    // (opcional) “ir” a una semana cuando el texto coincide exacto, y hacer scroll
    jumpIfExact() {
      const q = (this.search || '').trim().toLowerCase();
      if (!q) return;
      const exact = this.weeksForYear.find(w => this.matchesWeek(this.year, w, q) && (
        q === `semana ${String(w).padStart(2,'0')}` || q === `${w}`
      ));
      if (exact) {
        // centra esa tarjeta en el scroll
        this.$nextTick(() => {
          const list = this.$refs.scrollList;
          if (!list) return;
          const idx = this.weeksForYearFiltered.indexOf(exact);
          if (idx >= 0) {
            const itemHeight = 64; // aprox item + divider
            list.scrollTop = Math.max(0, idx * itemHeight - 100);
          }
        });
      }
    },
    weekLabel(year, week) {
      return `Semana ${this.pad2(week)} (${year})`;
    },
    weekRangeLabel(year, week) {
      const r = this.isoRange(year, week);
      return `${r.desde} — ${r.hasta}`;
    },
    isSelected(year, week) {
      return this.value.some(s => s.year === year && s.week === week);
    },
    toggleWeek(week) {
      const idx = this.value.findIndex(s => s.year === this.year && s.week === week);
      if (idx >= 0) {
        const copy = [...this.value];
        copy.splice(idx, 1);
        this.$emit('input', copy);
      } else {
        const r = this.isoRange(this.year, week);
        this.$emit('input', [...this.value, { year: this.year, week, ...r }]);
      }
      // mantener temp al día
      this.syncTempWithValue();
    },
    removeWeek(year, week) {
      const copy = this.value.filter(s => !(s.year === year && s.week === week));
      this.$emit('input', copy);
      this.syncTempWithValue();
    },
    clearAll() { this.$emit('input', []); this.syncTempWithValue(); },
    selectAllVisible() {
      const toAdd = this.weeksForYear
        .filter(w => !this.isSelected(this.year, w))
        .map(w => ({ year: this.year, week: w, ...this.isoRange(this.year, w) }));
      this.$emit('input', [...this.value, ...toAdd]);
      this.syncTempWithValue();
    },
    clearVisible() {
      const copy = this.value.filter(s => s.year !== this.year);
      this.$emit('input', copy);
      this.syncTempWithValue();
    },
    emitSelection() { this.menu = false; },
    syncTempWithValue() {
      this.tempSelection = this.value.filter(s => s.year === this.year)
        .map(s => ({ year: s.year, week: s.week }));
    }
  }
};
</script>

<style scoped>
.v-list-item { cursor: pointer; }
</style>